import { Button } from '@mui/material';
import { FormFieldItemRecord } from 'config-types/config/form-fields';
import { Coverage, Patient } from 'fhir/r4b';
import { FC, ReactNode, useMemo } from 'react';
import { Section } from 'src/components/layout/Section';
import { dataTestIds } from 'src/constants/data-test-ids';
import { checkCoverageMatchesDetails } from 'utils/lib/fhir/billing';
import { PATIENT_RECORD_CONFIG } from 'utils/lib/ottehr-config/patient-record';
import { CoverageCheckWithDetails } from 'utils/lib/types/api/patient-account';
import { CoverageWithPriority } from 'utils/lib/types/data/account';
import { InsuranceContainer } from './InsuranceContainer';
import { SectionSaveButton } from './SectionSaveButton';

const insuranceSection = PATIENT_RECORD_CONFIG.FormFields.insurance;

const getInsuranceSectionDefinition = (index: number): { items: FormFieldItemRecord; requiredFields: string[] } => {
  const items = Array.isArray(insuranceSection.items) ? insuranceSection.items[index] : insuranceSection.items;
  return {
    items: items ?? {},
    requiredFields: insuranceSection.requiredFields ?? [],
  };
};

export const getEligibilityCheckDetailsForCoverage = (
  coverage: Coverage,
  coverageChecks: CoverageCheckWithDetails[]
): CoverageCheckWithDetails | undefined => {
  return coverageChecks.find((check) => checkCoverageMatchesDetails(coverage, check));
};

export const InsuranceSection: FC<{
  coverages: CoverageWithPriority[];
  patient: Patient;
  accountData: any;
  removeCoverage: any;
  onRemoveCoverage: (coverageId: string) => void;
  isAddingInsurance: boolean;
  onStartAddInsurance: () => void;
  onCancelAddInsurance: () => void;
  onCloseAddInsurance: () => void;
  newInsuranceOrdinal: number;
  encounterId?: string;
  /**
   * Visit-page-only: renders a coverage's compact card thumbnail inside its insurance block.
   * Called with the 0-based card ordinal (0 = primary, 1 = secondary).
   */
  renderInsuranceCardThumbnail?: (ordinal: number) => ReactNode;
}> = ({
  coverages,
  patient,
  accountData,
  removeCoverage,
  onRemoveCoverage,
  isAddingInsurance,
  onStartAddInsurance,
  onCancelAddInsurance,
  onCloseAddInsurance,
  newInsuranceOrdinal,
  encounterId,
  renderInsuranceCardThumbnail,
}) => {
  const primary = getInsuranceSectionDefinition(0);
  const secondary = getInsuranceSectionDefinition(1);

  const fieldKeys = useMemo(() => {
    const renderedOrdinals = new Set<number>();
    coverages.forEach((c) => renderedOrdinals.add(c.startingPriority - 1));
    if (isAddingInsurance) renderedOrdinals.add(newInsuranceOrdinal - 1);

    const collected: string[] = [];
    [primary, secondary].forEach((section, index) => {
      if (!renderedOrdinals.has(index)) return;
      const keys = Object.values(section.items).map((item) => item.key);
      collected.push(...keys);
    });
    return collected;
  }, [coverages, isAddingInsurance, newInsuranceOrdinal, primary, secondary]);

  return (
    <Section
      title="Insurance information"
      titleWidget={
        <SectionSaveButton
          fieldKeys={fieldKeys}
          patientId={patient.id}
          encounterId={encounterId}
          onSaveSuccess={isAddingInsurance ? onCloseAddInsurance : undefined}
        />
      }
    >
      {coverages.map((coverage) => (
        <InsuranceContainer
          key={coverage.resource.id}
          patientId={patient.id ?? ''}
          ordinal={coverage.startingPriority}
          initialEligibilityCheck={getEligibilityCheckDetailsForCoverage(
            coverage.resource,
            accountData?.coverageChecks ?? []
          )}
          removeInProgress={removeCoverage.isPending}
          handleRemoveClick={
            coverage.resource.id !== undefined ? () => onRemoveCoverage(coverage.resource.id!) : undefined
          }
          renderWithoutSection
          renderInsuranceCardThumbnail={renderInsuranceCardThumbnail}
        />
      ))}
      {isAddingInsurance && (
        <InsuranceContainer
          patientId={patient.id ?? ''}
          ordinal={newInsuranceOrdinal}
          isNew
          onCancelAdd={onCancelAddInsurance}
          renderWithoutSection
          renderInsuranceCardThumbnail={renderInsuranceCardThumbnail}
        />
      )}
      {coverages.length < 2 && !isAddingInsurance && (
        <Button
          data-testid={dataTestIds.patientInformationPage.addInsuranceButton}
          variant="outlined"
          color="primary"
          onClick={onStartAddInsurance}
          sx={{
            borderRadius: 25,
            textTransform: 'none',
            fontWeight: 'bold',
            width: 'fit-content',
          }}
        >
          + Add Insurance
        </Button>
      )}
    </Section>
  );
};
